import React from 'react';
import { ContainerComponent, wrap, today } from 'AppReact';
import Controller from './controller'
import { SecondaryTextXs, Icon, RedTextXs, ProfileImage, Initials, ClickableIcon, PrimaryTextXs, Tooltip, Empty } from 'atoms';
import { format, isBefore, isSameDay } from 'date-fns';
import { SolidButton, TextButton } from 'molecules';
import { CheckboxSvg, GreenTickSvg, PlusSignSvg, CompleteTaskConfettiSvg, DownCaretSvg } from 'components/icons';
import internalState from './internalState';
import {
  MakeReadyItemCellWrapper, Td, Mr2, Mt3,
  FlexCenterWrapper, RelativeWrapper, Mt4,
  CustomPositionAbsoluteWrapper, BorderedCard, Mr5,
  FlexCenterBetweenWrapper, WhiteSpaceNoWrap,
  HoverPrimaryBackground50Wrapper, Px4, Py3, Mx2,
  TooltipWrapper, CompleteTaskConfettiWrapper
} from 'wrappers';
import { LabeledDatepicker, AssignWorkerDropdown, NoteInput, SlimNoteMessage } from 'containers';

const popupRef = React.createRef();

export const definition = {
  name: 'ItemCell',
  controller: Controller,
  initialProps: ({ item, techs, vendors, activeAdmins, displayConfetti, selectedRow, containerRef, admins, makeReadyCategory, showCheckboxes, hideCheckbox, isSelected, shouldOpen, selectedItems, selectedCategories }) => ({
    techs, vendors, activeAdmins, selectedRow, containerRef, hideCheckbox, selectedItems, selectedCategories,
    displayConfetti, item, admins, makeReadyCategory, showCheckboxes, isSelected, shouldOpen
  }),
  props: internalState,
  elements: [
    {
      wrapper: RelativeWrapper,
      name: 'RelativeWrapper',
      renderIf: ({ selectedRow }) => !selectedRow,
      ref: ({ containerRef, isOpen }) => isOpen ? containerRef : null,
      elements: [
        {
          wrapper: CompleteTaskConfettiWrapper,
          name: 'ConfettiWrapper',
          renderIf: ({ displayConfetti }) => displayConfetti,
          elements: [
            {
              component: Icon,
              name: 'Icon',
              displayValue: ({ }) => CompleteTaskConfettiSvg({})
            }
          ]
        },
        {
          wrapper: MakeReadyItemCellWrapper,
          name: 'Selector',
          renderIf: ({ displayConfetti }) => !displayConfetti,
          isButton: true,
          bindings: ({ isOpen, shouldOpen, containerRef, item }) => {
            return {
              isOpen,
              shouldOpen,
              item,
              containerRef,
              popupRef,
            }
          },
          displayState: ({ item, isOpen }) => {
            let background = '';
            if (item?.completed) {
              background = '#F4FCF8';
              if (isOpen) {
                background = '#C7EFDA';
              }
            } else if (item?.scheduled || item?.tech_id || item?.vendor_id || item?.admin_id) {
              background = '#FFFCF2';
              if (isOpen) {
                background = '#FFF1BF';
              }
            }
            return { background, isOpen }
          },
          elements: [
            {
              component: wrap(ClickableIcon, Mx2),
              name: 'Checkbox',
              bindings: ({ item, isOpen, isSelected, showCheckboxes, hideCheckbox }) => {
                return {
                  item,
                  isOpen,
                  showCheckboxes,
                  hideCheckbox,
                  isSelected
                }
              },
              renderIf: ({ isOpen, showCheckboxes, hideCheckbox }) => isOpen || (showCheckboxes && !hideCheckbox),
              displayValue: ({ isSelected }) => CheckboxSvg({ checked: isSelected })
            },
            {
              wrapper: wrap(FlexCenterWrapper, TooltipWrapper),
              name: 'InfoWrapper',
              renderIf: ({ isOpen }) => !isOpen,
              elements: [
                {
                  component: wrap(ProfileImage, Mr2),
                  name: 'TechImage',
                  renderIf: ({ item, isSelected }) => ((!!item?.tech_image || !!item?.vendor_url || !!item?.admin_image) && !item?.completed) && !isSelected,
                  displayValue: ({ item }) => item?.tech_image ?? item?.vendor_url ?? item?.admin_image,
                  displayState: () => ({ size: '20', primary: true })
                },
                {
                  component: wrap(Initials, Mr2),
                  name: 'TechInitials',
                  renderIf: ({ item, isSelected }) => (!(item?.tech_image || item?.vendor_url || item?.admin_image) && (item?.tech || item?.vendor || item?.admin) && !item?.completed) && !isSelected,
                  displayValue: ({ item }) => item?.tech ? item?.tech?.[0] : item?.vendor ? item?.vendor?.[0] : item?.admin[0],
                  displayState: () => ({ size: '20', primary: true })
                },
                {
                  component: SecondaryTextXs,
                  name: 'Scheduled',
                  renderIf: ({ item, isSelected }) => (item?.scheduled && !(isSameDay(item.scheduled, today()) || isBefore(item.scheduled, today())) && !item.completed) && !isSelected,
                  displayValue: ({ item }) => format(item.scheduled, 'MM/dd/yy')
                },
                {
                  component: RedTextXs,
                  name: 'Scheduled',
                  renderIf: ({ item, isSelected }) => (item?.scheduled && (isSameDay(item.scheduled, today()) || isBefore(item.scheduled, today())) && !item.completed) && !isSelected,
                  displayValue: ({ item }) => format(item.scheduled, 'MM/dd/yy')
                },
                {
                  component: Empty,
                  name: 'Completed',
                  renderIf: ({ item, isOpen, isSelected }) => (item?.completed && !isOpen) && !isSelected,
                  displayValue: ({ }) => <GreenTickSvg color={'#1DBD6B'} />
                },
                {
                  component: Tooltip,
                  name: 'Popup',
                  renderIf: ({ item }) => (item?.tech || item?.vendor) && !item.completed,
                  displayValue: ({ item }) => `Assigned to ${item?.tech ?? item?.vendor ?? ''}`
                }
              ]
            },
          ]
        },
        {
          wrapper: CustomPositionAbsoluteWrapper,
          name: "DropdownWrapper",
          displayState: ({ top, left, bottom }) => ({ top, left, bottom }),
          renderIf: ({ isOpen }) => isOpen,
          ref: popupRef,
          elements: [
            {
              wrapper: BorderedCard,
              name: 'Wrapper',
              displayState: ({ }) => ({ background: '#FFFFFF', borderRadius: '8px', padding: '24px', width: '370px' }),
              elements: [
                {
                  wrapper: FlexCenterBetweenWrapper,
                  name: 'ButtonsWrapper',
                  elements: [
                    {
                      wrapper: FlexCenterWrapper,
                      name: 'CompleteButtonWrapper',
                      elements: [
                        {
                          component: wrap(SolidButton, Mr5),
                          name: 'Complete',
                          bindings: ({ item }) => ({ title: item?.completed ? 'Reopen' : 'Complete task' })
                        },
                      ]
                    },
                    {
                      component: wrap(TextButton, WhiteSpaceNoWrap),
                      name: 'Timeline',
                      bindings: ({ }) => ({ title: 'View details' })
                    }
                  ]
                },
                {
                  component: wrap(LabeledDatepicker, Mt4),
                  name: 'ScheduledDate',
                  renderIf: ({ item }) => !item?.completed,
                  bindings: ({ item }) => ({
                    item,
                    selectedDate: item?.scheduled,
                    label: { values: ['Schedule: '], keys: ['text'], component: SecondaryTextXs },
                    disabledBefore: today(),
                    hasRemove: true
                  })
                },
                {
                  component: wrap(AssignWorkerDropdown, Mt3),
                  name: 'Assign',
                  renderIf: ({ item }) => !item?.completed,
                  bindings: ({ item, techs, vendors, admins, makeReadyCategory }) => {
                    return {
                      item,
                      techs: techs,
                      vendors: vendors,
                      admins: admins,
                      label: 'Assign: ',
                      hideAdmins: !makeReadyCategory.can_be_assigned_to_admin,
                      hideTechs: !makeReadyCategory.can_be_assigned_to_tech,
                      hideVendors: !makeReadyCategory.can_be_assigned_to_vendor,
                      assignedTechId: item?.tech_id,
                      assignedVendorId: item?.vendor_id,
                      assignedAdminId: item?.admin_id,
                      textHeader: false,
                    }
                  }
                },
                {
                  component: wrap(NoteInput, Mt3),
                  name: "NoteInput",
                  renderIf: ({ item, displayNoteInput }) => !item?.completed && displayNoteInput,
                  bindings: ({ activeAdmins, item }) => {
                    let personsToMention = activeAdmins?.map(a => ({ name: a.name, email: a.email, id: a.id })) ?? []
                    if (item?.tech && item?.tech_id) {
                      personsToMention = [{ name: `${item.tech} (Technician)`, id: item.tech_id }, ...activeAdmins?.map(a => ({ name: a.name, email: a.email, id: a.id })) ?? []]
                    }
                    return {
                      personsToMention,
                      item,
                      placeholder: 'Use @ to mention someone'
                    }
                  }
                },
                {
                  wrapper: wrap(wrap(wrap(wrap(FlexCenterBetweenWrapper, Py3), Px4), HoverPrimaryBackground50Wrapper), Mt3),
                  name: 'NoteAdded',
                  isButton: true,
                  bindings: ({ }) => ({}),
                  renderIf: ({ item, displayNoteInput }) => !displayNoteInput && !item?.completed,
                  elements: [
                    {
                      component: PrimaryTextXs,
                      name: 'Text',
                      displayValue: ({ }) => 'Note added'
                    },
                    {
                      component: Icon,
                      name: 'Icon',
                      displayValue: ({ }) => PlusSignSvg({ withoutBox: true })
                    }
                  ]
                },
                {
                  wrapper: wrap(FlexCenterWrapper, Mt4),
                  name: 'ViewNotes',
                  renderIf: ({ item }) => item?.events?.filter(e => e.event_name === 'comment' || e.event_name === 'created').length > 0,
                  elements: [
                    {
                      component: wrap(TextButton, Mr2),
                      name: 'Button',
                      bindings: ({ displayNotes }) => ({ displayNotes, title: displayNotes ? 'Hide notes' : 'View notes' })
                    },
                    {
                      component: Icon,
                      name: 'Icon',
                      displayValue: ({ }) => DownCaretSvg({})
                    }
                  ]
                },
                {
                  component: wrap(SlimNoteMessage, Mt3),
                  name: 'NoteMessage',
                  renderIf: ({ displayNotes }) => displayNotes,
                  isListOf: { event: ({ item }) => item?.events?.filter(e => e.event_name === 'comment').reverse().slice(0, 2) },
                  bindings: ({ event }) => ({ event, currentUser: window.current_user })
                }
              ]
            }
          ]
        }
      ]
    }
  ]
}

export default wrap(wrap(ContainerComponent(definition), RelativeWrapper), Td)
